import React, { FunctionComponent } from 'react'
import { defineMessage, FormattedMessage } from 'react-intl'

import { noop } from 'lodash-es'
import styled from 'styled-components/native'

import {
  CDN_ASSETS_LINK_BASE,
  CoverageType,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { WellnessCheckInDomain } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/constants'
import { WellnessCheckInResultsCostMessagingProps } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/types'
import {
  getLowerCaseDomainTitle,
  getProviderTabConfigurationMsg,
  getProviderTabLowScoreMsg,
} from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/utils'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { CoverageMessagingLink } from '../coverageMessagingLink/CoverageMessagingLink'
import { WellnessCheckInResultsEssentialsCard } from '../wellnessCheckInResultsEssentialsCard/WellnessCheckInResultsEssentialsCard'

export interface WellnessCheckInResultsProviderTabContentProps {
  lowScoreDomains: WellnessCheckInDomain[]
  costMessagingProps?: WellnessCheckInResultsCostMessagingProps
  onProviderTabCardPress?: () => void
}

const ContentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['16px'],
}))

export const WellnessCheckInResultsProviderTabContent: FunctionComponent<
  WellnessCheckInResultsProviderTabContentProps
> = ({ lowScoreDomains, costMessagingProps, onProviderTabCardPress = noop }) => {
  const {
    isDependentDisabled,
    numSessions,
    numberOfSessionByProgram,
    coverageModalTitleText,
    coverageModalContents,
    isCostMessagingEnabled,
    costMessagingModalCoverageInfo,
    trackCoverageModalPress = noop,
  } = costMessagingProps || {}

  const nonEAPOrDualSessionEntitlement =
    costMessagingModalCoverageInfo?.customerCoverageType === CoverageType.HPI_ONLY || Boolean(numberOfSessionByProgram)
  const showCoverageMessagingLink = isCostMessagingEnabled && nonEAPOrDualSessionEntitlement

  return (
    <ContentContainer testID={tID('WellnessCheckInResultsProviderTabContent')}>
      <BodyText
        text={
          <FormattedMessage
            {...getProviderTabLowScoreMsg(lowScoreDomains.length)}
            values={{
              firstDomain: lowScoreDomains.length > 0 ? getLowerCaseDomainTitle(lowScoreDomains[0]) : '',
              secondDomain: lowScoreDomains.length > 1 ? getLowerCaseDomainTitle(lowScoreDomains[1]) : '',
              showCoverageMessagingLink: showCoverageMessagingLink,
              coverageMessagingLink: () => {
                if (showCoverageMessagingLink && coverageModalContents && coverageModalContents.length > 0) {
                  return (
                    <CoverageMessagingLink
                      coverageModalContents={coverageModalContents}
                      coverageModalTitleText={coverageModalTitleText}
                      costMessagingModalCoverageInfo={costMessagingModalCoverageInfo}
                      isCostMessagingEnabled={isCostMessagingEnabled}
                      trackCoverageModalPress={trackCoverageModalPress}
                    />
                  )
                }
                return null
              },
            }}
          />
        }
        size={BodyTextSize.SMALL}
      />
      {!nonEAPOrDualSessionEntitlement && (
        <BodyText
          text={
            <FormattedMessage
              {...getProviderTabConfigurationMsg({
                isDependentDisabled: Boolean(isDependentDisabled),
              })}
              values={{
                numSessions: numSessions,
              }}
            />
          }
          size={BodyTextSize.SMALL}
        />
      )}
      <WellnessCheckInResultsEssentialsCard
        articleContent={{
          title: defineMessage({
            defaultMessage: 'Get matched to providers',
            description: 'Title for card that prompts user to connect with a provider for care',
          }),
          description: defineMessage({
            defaultMessage: 'Share your needs—we’ll match you with mental health experts',
            description: 'Description for card that prompts user to connect with a provider for care',
          }),
          imageUrl: `${CDN_ASSETS_LINK_BASE}/matchToProviders.png`,
        }}
        onCardPress={onProviderTabCardPress}
        descriptionNumberOfLines={2}
        testID={'WellnessCheckInResultsProviderTabContentCard'}
      />
    </ContentContainer>
  )
}
